import { type GetSimulationResponse, SimulationStatus } from '@/api';
import { Permission } from '@/api';
import PermissionWrapper from '@/components/auth/permission-wrapper';
import { useAuthContext } from '@/context/auth-context';
import { useUserRole } from '@/hooks/use-user-role';
import { connectColors } from '@/theme/colors';
import { Button } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import type { RefObject } from 'react';
import { StatusTag } from '../../../simulation-list-page/components/status-tag';
import { SYSTEM_ASSIGNED_STATUSES } from '../../../utils/simulation-status-helpers';
import { useSimulationReadOnly } from '../../context/simulation-read-only-context';

interface SimulationHeaderTitleProps {
  simulationDetails: GetSimulationResponse;
  sendToTriggerRef: RefObject<HTMLButtonElement | null>;
  onSendToClick: () => void;
}

const SimulationHeaderTitle = ({
  simulationDetails,
  sendToTriggerRef,
  onSendToClick,
}: SimulationHeaderTitleProps) => {
  const { isReadOnly } = useSimulationReadOnly();
  const { user } = useAuthContext();
  const { isGlobalPriceManager } = useUserRole();
  const isAssignee =
    user?.userId != null && user.userId === simulationDetails.assignedTo.id;

  const changeStatusLabel = isGlobalPriceManager
    ? 'Send to Local Review'
    : 'Send to Global Review';

  // Global managers can send to Local Review unless already there; locals can only send from Local Review.
  const isStatusTransitionBlocked = isGlobalPriceManager
    ? simulationDetails.status === SimulationStatus.LocalReview
    : simulationDetails.status !== SimulationStatus.LocalReview;

  const isSendToDisabled =
    !isAssignee ||
    isReadOnly ||
    isStatusTransitionBlocked ||
    (simulationDetails.status != null &&
      SYSTEM_ASSIGNED_STATUSES.includes(simulationDetails.status));

  return (
    <Flex alignItems={'center'} gap="6px">
      <Text
        fontSize="md"
        fontWeight="medium"
        color={connectColors.core.offBlack}
      >
        {simulationDetails.simulationName}
      </Text>
      <div style={{ margin: '0 0 0 10px' }}>
        <StatusTag status={simulationDetails.status} size={'small'} />
      </div>
      <PermissionWrapper requiredPermissions={[Permission.ReadSimulations]}>
        <Button
          aria-label={changeStatusLabel}
          iconName={'arrow-right-arrow-left-solid'}
          iconPosition="end"
          disabled={isSendToDisabled}
          appearance="ghost"
          size={'small'}
          style={{
            color: connectColors.core.blue[1000],
          }}
          ref={sendToTriggerRef}
          onClick={onSendToClick}
        >
          {changeStatusLabel}
        </Button>
      </PermissionWrapper>
    </Flex>
  );
};

export default SimulationHeaderTitle;
